// Pre-rendered preroll buffer + scheduling helper.
//
// Why this exists (FB#25, third pass): even with pre-decoded BufferSources,
// scheduling THREE separate `start(t)` calls leaves three independent
// scheduling events each with their own jitter surface. Edge sometimes
// delayed beep #2 by ~50-150ms, Safari occasionally dropped one entirely.
//
// New approach: render the ENTIRE preroll (all N beeps + the silent gaps
// between them) into a SINGLE AudioBuffer. The recording flow then plays
// it with ONE `src.start(t0)` — sample-accurate spacing inside the buffer
// is guaranteed by the offline render, no per-beep scheduling needed.
//
// Trade-off: a 3-beep / 1000ms / 48kHz mono buffer is 3s * 48000 * 4B = 576KB,
// negligible vs the playback reliability win.

import {
  ensureVoiceGain,
  getAudioContext,
  isSafariMediaStreamPath,
  onPreviewAudioInvalidate,
  startSafariAudioTap,
} from '@/lib/audioContext';
import { effectiveVoiceVolume, useMixerStore } from '@/stores/mixer';

const BEEP_FREQ_HZ = 1000;
const BEEP_LENGTH_MS = 90;
const RENDER_SAMPLE_RATE = 48_000;

/** Default config — matches the only production caller (RecordingStudio).
 *  Pre-rendered at module load time via `preloadBeepBuffer()`. */
const DEFAULT_BEEPS = 3;
const DEFAULT_INTERVAL_MS = 1000;

/** Cache key: `${beeps}-${intervalMs}`. Different configs render distinct
 *  buffers — typically only one entry in production. */
const cache = new Map<string, AudioBuffer>();
const pending = new Map<string, Promise<AudioBuffer>>();

function cacheKey(beeps: number, intervalMs: number): string {
  return `${beeps}-${intervalMs}`;
}

// When the shared AudioContext is invalidated (master timeline rebuild after
// timeline play, video export, large-blob memory pressure on Safari) the
// cached AudioBuffers are bound to the now-closed context. Safari sometimes
// pays a wake-up latency on the first schedule of a context-foreign buffer
// — symptom: first beep after a master-timeline play session lands slightly
// late. Drop the cache + immediately re-render so the next preroll is hot.
onPreviewAudioInvalidate(() => {
  cache.clear();
  pending.clear();
  // Fire-and-forget: render the default config so RecordingStudio's next
  // play hits a fresh, context-correct buffer instead of paying the cold-
  // render latency.
  void preloadBeepBuffer().catch((e) =>
    console.warn('[preroll] re-render after invalidate failed', e),
  );
});

/** Render the preroll buffer for the given config if not already cached.
 *  Eager-called once via `eagerInit()` with the default (3, 1000) config. */
export async function preloadBeepBuffer(
  beeps: number = DEFAULT_BEEPS,
  intervalMs: number = DEFAULT_INTERVAL_MS,
): Promise<AudioBuffer> {
  const key = cacheKey(beeps, intervalMs);
  const cached = cache.get(key);
  if (cached) return cached;
  const inflight = pending.get(key);
  if (inflight) return inflight;

  const p = renderPreroll(beeps, intervalMs).then(
    (buf) => {
      cache.set(key, buf);
      pending.delete(key);
      return buf;
    },
    (err) => {
      pending.delete(key);
      throw err;
    },
  );
  pending.set(key, p);
  return p;
}

/** Render an N-beep preroll into a single AudioBuffer. Beep i starts at
 *  i * intervalMs; buffer length = beeps * intervalMs (so buffer-end = the
 *  "go" moment, one interval after the last beep). */
async function renderPreroll(
  beeps: number,
  intervalMs: number,
): Promise<AudioBuffer> {
  const totalMs = beeps * intervalMs;
  const lengthSamples = Math.ceil((totalMs / 1000) * RENDER_SAMPLE_RATE);
  const offline = new OfflineAudioContext(1, lengthSamples, RENDER_SAMPLE_RATE);

  for (let i = 0; i < beeps; i++) {
    const startS = (i * intervalMs) / 1000;
    const endS = startS + BEEP_LENGTH_MS / 1000;
    // 5ms attack + 30ms release envelope on each beep — same shape as the
    // single-beep render, baked into the per-beep slot of the master buffer.
    const attackS = startS + 0.005;
    const releaseStartS = endS - 0.03;

    const osc = offline.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = BEEP_FREQ_HZ;

    const gain = offline.createGain();
    gain.gain.setValueAtTime(0, startS);
    gain.gain.linearRampToValueAtTime(1, attackS);
    gain.gain.setValueAtTime(1, releaseStartS);
    gain.gain.linearRampToValueAtTime(0, endS);

    osc.connect(gain).connect(offline.destination);
    osc.start(startS);
    osc.stop(endS);
  }

  return offline.startRendering();
}

export interface BeepScheduleHandle {
  /** Wall-clock `performance.now()` at which the "go" moment falls. Initially
   *  set to a sentinel far in the future so the visual countdown stays at the
   *  starting digit; UPDATED to the real value once the audio is actually
   *  scheduled (Safari's resume() can take 100s of ms after a suspend). The
   *  visual countdown should poll this each tick to stay in sync. */
  endsAt: number;
  /** Resolves at the visible "go" moment (countdown hits 0). Prefer this
   *  over `setTimeout(endsAt - perfNow)` since endsAt may shift forward if
   *  the AudioContext takes time to resume after the user gesture. */
  go: Promise<void>;
  /** Resolves once the audible portion is done + the BufferSource has been
   *  disconnected. Callers should `void` this. */
  settle: Promise<void>;
  /** Stop + disconnect the scheduled source (e.g. user cancelled recording
   *  mid-countdown). Idempotent. */
  cancel: () => void;
}

export interface BeepScheduleOptions {
  beeps?: number;       // default DEFAULT_BEEPS
  intervalMs?: number;  // default DEFAULT_INTERVAL_MS
  /** Linear gain applied to the buffer. 0..1, default 0.3 — matches the
   *  perceived loudness of the previous oscillator-with-envelope path. */
  gain?: number;
}

/** Schedule the pre-rendered preroll on the shared AudioContext with a SINGLE
 *  `src.start(t0)` call. Synchronous — must be called inside the user gesture
 *  that triggered the recording.
 *
 *  All beep timing is sample-accurate within the rendered buffer, so we get
 *  perfect inter-beep sync regardless of what the audio thread does after
 *  start() — no per-beep scheduling jitter, no dropped events.
 *
 *  Cold path (buffer not yet rendered): kicks off the render and schedules
 *  once it lands. The visual countdown's `endsAt` is computed from the same
 *  `t0` we use for `start()`, so audio + visual stay in lockstep even on the
 *  cold path. */
export function scheduleBeeps(opts: BeepScheduleOptions = {}): BeepScheduleHandle {
  const {
    beeps = DEFAULT_BEEPS,
    intervalMs = DEFAULT_INTERVAL_MS,
    gain = 0.3,
  } = opts;
  const ac = getAudioContext();

  const masterGain = ac.createGain();
  masterGain.gain.value = gain;
  // Route beeps through the shared voiceGain when available so they ride
  // the same Safari MediaStream tap (audio-element bridge) the rest of
  // the voice path uses. Going straight to ac.destination — which is
  // what we used to do — silently degrades on Safari under memory
  // pressure / large blob load (the documented quirk that voiceGain +
  // streamDest were built to bypass). Fall back to ac.destination if
  // voiceGain belongs to a different context (post-invalidate window).
  const mix = effectiveVoiceVolume(useMixerStore.getState());
  const voiceGain = ensureVoiceGain(mix);
  if (voiceGain.context === ac) {
    masterGain.connect(voiceGain);
    if (isSafariMediaStreamPath()) startSafariAudioTap();
  } else {
    masterGain.connect(ac.destination);
  }

  let source: AudioBufferSourceNode | null = null;
  let cancelled = false;

  const RUNNING_OFFSET_S = 0.05;
  const SUSPENDED_OFFSET_S = 0.15;
  const totalMs = beeps * intervalMs;

  // Sentinel: huge endsAt so the visual countdown freezes at the starting
  // digit until we know the actual schedule. Resume on Safari takes ~100ms
  // typically but has been observed >1s after a suspend — without this,
  // the visual would tick down before the first beep is even scheduled.
  const SENTINEL_ENDS_AT = performance.now() + 1_000_000;

  let resolveGo: () => void;
  const go = new Promise<void>((r) => { resolveGo = r; });
  let resolveSettle: () => void;
  const settle = new Promise<void>((r) => { resolveSettle = r; });

  const handle: BeepScheduleHandle = {
    endsAt: SENTINEL_ENDS_AT,
    go,
    settle,
    cancel: () => {
      if (cancelled) return;
      cancelled = true;
      try { source?.stop(); } catch { /* already stopped */ }
      try { source?.disconnect(); } catch { /* ignore */ }
      try { masterGain.disconnect(); } catch { /* ignore */ }
      resolveGo();
      resolveSettle();
    },
  };

  // Schedule the buffer once we have BOTH a running context AND the rendered
  // buffer. Re-reading currentTime here is critical: when the ctx was
  // suspended at gesture time, ac.currentTime was frozen, and computing t0
  // from the frozen value would put start(t) in the past once resume lands.
  function scheduleNow(buf: AudioBuffer): void {
    if (cancelled) return;
    const offsetS =
      ac.state === 'running' ? RUNNING_OFFSET_S : SUSPENDED_OFFSET_S;
    const when = ac.currentTime + offsetS;
    const src = ac.createBufferSource();
    src.buffer = buf;
    src.connect(masterGain);
    try {
      src.start(when);
    } catch {
      try { src.start(); } catch { /* give up */ }
    }
    source = src;

    // Now we know exactly when audio plays. Anchor endsAt + go promise to
    // the same wall-clock moment so the visual countdown ticks in lockstep.
    const startedAtPerf = performance.now();
    const offsetMs = offsetS * 1000;
    handle.endsAt = startedAtPerf + offsetMs + totalMs;

    window.setTimeout(resolveGo, offsetMs + totalMs);
    window.setTimeout(() => {
      try { source?.disconnect(); } catch { /* ignore */ }
      try { masterGain.disconnect(); } catch { /* ignore */ }
      resolveSettle();
    }, offsetMs + totalMs + BEEP_LENGTH_MS + 100);
  }

  // Sync-call resume inside the user gesture so Safari accepts it; AWAIT
  // before scheduling. After resume, currentTime reflects the post-resume
  // clock, not the frozen suspended one.
  const resumeP: Promise<void> =
    ac.state === 'running'
      ? Promise.resolve()
      : ac.resume().catch((e) => {
          console.warn('[preroll] resume failed', e);
        });

  const cached = cache.get(cacheKey(beeps, intervalMs));
  const bufferP: Promise<AudioBuffer | null> = cached
    ? Promise.resolve(cached)
    : preloadBeepBuffer(beeps, intervalMs).catch((e) => {
        console.warn('[preroll] render failed', e);
        return null;
      });

  void Promise.all([resumeP, bufferP]).then(([, buf]) => {
    if (cancelled || !buf) return;
    scheduleNow(buf);
  });

  return handle;
}
